import { matchPath } from 'react-router'
import { observer, inject } from 'mobx-react'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import InlineSVG from 'svg-inline-react'

import avatar from '../../../assets/icons/AvatarSVG'
import heyNicBubble from '../../../assets/images/heyNicBubbleDefault.png'
import heyNicBubbleHover from '../../../assets/images/heyNicBubbleHover.png'
import guidedChoiceLogo from '../../../assets/images/GuidedChoice-logo.png'
import { SmallSecondaryButton } from '../../../components/Button'
import { API_URL } from '../../../api'
import { MobileDrawer } from './sidebar'
import { withRouterProps } from '../../../utils'
import { TABLET } from '../../../guided-toolbox/utils/breakpoints'

const LogoutButton = styled(SmallSecondaryButton)`
  min-width: 0;
  margin-left: 10px;
  margin-right: 15px;
  padding: 0 18px;
  font-size: 1rem;
  font-weight: 400;
`

const Box = styled.div`
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin: auto;
  border: ${p => p.theme.topNavBorder};
  box-shadow: 0 2px 2px 0 rgba(182, 192, 196, 0.15);
  ${TABLET} {
    height: 64px;
    justify-content: flex-start;
  }
  background-color: ${p => p.theme.topNavColor};
`
const LogoContainer = styled.div`
  * {
    max-width: 220px;
    min-width: 175px;
  }
  padding: 20px;
  cursor: pointer;
`

const Logo = styled.img`
  width: 100%;
  /* The default theme has no API logo, so use the app’s GuidedChoice logo. */
  content: url(${p => (p.$brandLogo ? guidedChoiceLogo : API_URL + p.theme.logo)});
`

const MenuBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-x: auto;
  ${TABLET} {
    display: none;
  }
`
const MenuItem = styled.div`
  cursor: pointer;
  padding: 20px;
  white-space: nowrap;
  transition:
    color 0.35s,
    padding 0.35s;
  @media (max-width: 900px) {
    padding: 10px;
  }
  color: ${p => (p.isActive ? p.theme.primaryColor : p.theme.topNavTextColor)};
`
const ProfileBox = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 15px;
  transition: 0.35s;
  color: ${p => (p.isActive ? p.theme.primaryColor : p.theme.topNavTextColor)};
  min-width: 90px;
  column-gap: 3px;
`
const AvatarBox = styled.div`
  min-width: 28px;
  width: 28px;
  height: 28px;
  padding: 4px;
  & svg {
    transition: 0.35s;
    stroke: ${p => (p.isActive ? p.theme.primaryColor : `#7a8e96`)};
    fill: ${p => (p.isActive ? p.theme.primaryColor : `#7a8e96`)};
  }
`
const TopNav = props => {
  const location = props.location?.pathname ?? ''
  const {
    navigate,
    store: { features, config, aiStatus, hasPlanAccess },
  } = props
  const overallStrategyPaths = ['/overall', '/spending']
  const overallStrategyIsActive = overallStrategyPaths.some(path => matchPath({ path }, location))
  const isSpendDownContext = config.isSpendown || config.spendDownModeling

  const handleLogout = () => {
    window.open(`${API_URL.slice(0, -4)}logout`, '_self')
  }

  const ensureSideOpen = () => {
    if (!props.main.enableSidebar) props.main.setSidebar(true)
  }

  const onClickHome = () => {
    navigate('/home')
    ensureSideOpen()
  }

  const onClickOverallStrategy = () => {
    navigate('/overall')
    ensureSideOpen()
  }
  const onClickGoals = () => {
    navigate('/goals')
    ensureSideOpen()
  }
  const onClickAccounts = () => {
    navigate('/accounts')
    ensureSideOpen()
  }
  const onClickTools = () => {
    navigate('/tools')
    ensureSideOpen()
  }
  const onClickHeyNic = () => {
    // Remember where we came from so Exit can return there.
    navigate('/hey-nic', { state: { from: location || '/home' } })
  }
  const onClickProfile = () => {
    navigate('/profile')
    ensureSideOpen()
  }

  if (props.main.enableTopNav !== undefined && props.main.enableTopNav === false) {
    return null
  }

  return (
    <Box>
      <MobileDrawer />
      <LogoContainer onClick={onClickHome}>
        <Logo alt='Logo' $brandLogo={!hasPlanAccess} />
      </LogoContainer>
      <MenuBox>
        {features.enableHomePage &&
          (!features.enablePersonalizedTargetDatePlus || !isSpendDownContext) && (
            <MenuItem isActive={matchPath({ path: '/home' }, location)} onClick={onClickHome}>
              Home
            </MenuItem>
          )}

        {isSpendDownContext && hasPlanAccess && (
          <MenuItem isActive={overallStrategyIsActive} onClick={onClickOverallStrategy}>
            Overall Strategy
          </MenuItem>
        )}

        {features.enableGoals && hasPlanAccess && (
          <MenuItem isActive={matchPath({ path: '/goals' }, location)} onClick={onClickGoals}>
            Goals
          </MenuItem>
        )}

        {features.enableAccounts && hasPlanAccess && (
          <MenuItem isActive={matchPath({ path: '/accounts' }, location)} onClick={onClickAccounts}>
            Accounts
          </MenuItem>
        )}

        {features.enableTools && (
          <MenuItem isActive={matchPath({ path: '/tools' }, location)} onClick={onClickTools}>
            Tools
          </MenuItem>
        )}

        <ProfileBox isActive={matchPath({ path: '/profile' }, location)} onClick={onClickProfile}>
          <AvatarBox isActive={matchPath({ path: '/profile' }, location)}>
            <InlineSVG src={avatar} element='span' />
          </AvatarBox>
          <span
            css={css`
              text-overflow: ellipsis;
            `}>
            Profile
          </span>
        </ProfileBox>

        {/* Placed immediately left of the single trailing control (Logout) so the
            graphic sits at the same horizontal spot as in the focused Hey Nic top
            nav (where it sits just left of Exit) — it doesn't jump on navigation. */}
        {features.enableHeyNic && aiStatus?.available && (
          <MenuItem
            onClick={onClickHeyNic}
            css={css`
              display: flex;
              align-items: center;
              padding: 8px 16px;
              & img {
                display: block;
                height: 40px;
                width: auto;
              }
              & .hey-nic-hover {
                display: none;
              }
              &:hover .hey-nic-default {
                display: none;
              }
              &:hover .hey-nic-hover {
                display: block;
              }
            `}>
            <img src={heyNicBubble} className='hey-nic-default' alt='Hey Nic AI' />
            <img src={heyNicBubbleHover} className='hey-nic-hover' alt='Hey Nic AI' />
          </MenuItem>
        )}

        <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
      </MenuBox>
    </Box>
  )
}

export default withRouterProps(inject('store', 'main')(observer(TopNav)))
